/**
 * Funções auxiliares para manipulação de datas
 */

/**
 * Retorna a data de hoje no formato YYYY-MM-DD
 * @returns {string} - Data de hoje
 */
export const getTodayDateKey = () => {
    // Retorna YYYY-MM-DD usando o horário LOCAL do usuário
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

/**
 * Retorna o primeiro dia da semana (Segunda-feira)
 * @param {string} dateString - Data no formato YYYY-MM-DD
 * @returns {string} - Data da segunda-feira
 */
export const getStartOfWeek = (dateString) => {
    if (!dateString) return getTodayDateKey();
    const date = new Date(dateString.replace(/-/g, '/'));
    if (isNaN(date.getTime())) return getTodayDateKey();
    
    date.setHours(0, 0, 0, 0);
    let day = date.getDay();

    // Mapeia Domingo (0) para 7
    day = day === 0 ? 7 : day;

    // Ajusta para a segunda-feira
    const diff = date.getDate() - day + 1;
    date.setDate(diff);
    try {
        return date.toISOString().split('T')[0];
    } catch (e) {
        return getTodayDateKey();
    }
};

/**
 * Retorna o último dia da semana (Domingo)
 * @param {string} dateString - Data no formato YYYY-MM-DD
 * @returns {string} - Data do domingo
 */
export const getEndOfWeek = (dateString) => {
    if (!dateString) return getTodayDateKey();
    const startDateKey = getStartOfWeek(dateString);
    const date = new Date(startDateKey.replace(/-/g, '/'));
    if (isNaN(date.getTime())) return getTodayDateKey();
    
    date.setHours(0, 0, 0, 0);
    date.setDate(date.getDate() + 6);
    try {
        return date.toISOString().split('T')[0];
    } catch (e) {
        return getTodayDateKey();
    }
};

/**
 * Retorna o primeiro dia do mês
 * @param {string} yearMonth - Ano-Mês no formato YYYY-MM
 * @returns {string} - Data do primeiro dia
 */
export const getStartOfMonth = (yearMonth) => {
    return `${yearMonth}-01`;
};

/**
 * Retorna o último dia do mês
 * @param {string} yearMonth - Ano-Mês no formato YYYY-MM
 * @returns {string} - Data do último dia
 */
export const getEndOfMonth = (yearMonth) => {
    const [year, month] = yearMonth.split('-');
    const lastDay = new Date(parseInt(year), parseInt(month), 0).getDate();
    return `${yearMonth}-${String(lastDay).padStart(2, '0')}`;
};

/**
 * Retorna o primeiro dia do ano
 * @param {string} year - Ano (YYYY)
 * @returns {string} - Data do primeiro dia
 */
export const getStartOfYear = (year) => {
    return `${year}-01-01`;
};

/**
 * Retorna o último dia do ano
 * @param {string} year - Ano (YYYY)
 * @returns {string} - Data do último dia
 */
export const getEndOfYear = (year) => {
    return `${year}-12-31`;
};

/**
 * Verifica se uma data está dentro de um período
 * @param {string} date - Data a verificar
 * @param {string} start - Data inicial
 * @param {string} end - Data final
 * @returns {boolean} - True se estiver no período
 */
export const isDateInRange = (date, start, end) => {
    return date >= start && date <= end;
};

/**
 * Retorna o mês anterior
 * @returns {Object} - {year, month, key}
 */
export const getPreviousMonth = () => {
    const now = new Date();
    const prevDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    return {
        year: prevDate.getFullYear(),
        month: prevDate.getMonth() + 1,
        key: `${prevDate.getFullYear()}-${String(prevDate.getMonth() + 1).padStart(2, '0')}`
    };
};

/**
 * Calcula dias restantes no mês
 * @returns {number} - Número de dias restantes
 */
export const getRemainingDaysInMonth = () => {
    const today = new Date();
    const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate();
    return lastDay - today.getDate() + 1;
};

/**
 * Retorna o próximo mês formatado (YYYY-MM)
 * @param {string} currentMonthKey - Mês atual no formato YYYY-MM
 * @returns {string} - Próximo mês no formato YYYY-MM
 */
export const getNextMonth = (currentMonthKey) => {
    const [year, month] = currentMonthKey.split('-').map(Number);
    const date = new Date(year, month, 1); // month is 0-indexed in Date, but input is 1-indexed? No, input '2025-01'.
    // If input is '2025-01':
    // new Date(2025, 1, 1) -> Feb 1st 2025 (Month is index 1 = Feb)
    // format: 2025-02. Correct.
    // If input is '2025-12':
    // new Date(2025, 12, 1) -> Jan 1st 2026.
    // format: 2026-01. Correct.
    
    const nextYear = date.getFullYear();
    const nextMonth = String(date.getMonth() + 1).padStart(2, '0');
    return `${nextYear}-${nextMonth}`;
};
